import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  IconButton,
  Collapse,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Language,
  Add,
  ListAlt,
  ExitToApp,
  ExpandLess,
  ExpandMore,
  Dashboard,
  AdminPanelSettings,
  Storage,
  Assignment,
  History,
  DarkMode,
  LightMode,
  Block,
  ContactMail,
  Subscriptions,
  HealthAndSafety,
  Link as LinkIcon,
  Public,
  CloudQueue
} from '@mui/icons-material';
import { useAuth } from '../services/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ConfirmDialog from './ConfirmDialog';

const DRAWER_WIDTH = 280;

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();

  const [sitesOpen, setSitesOpen] = useState(true);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [formsOpen, setFormsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: 'primary.main',
          color: 'white',
          borderRight: 'none',
          boxShadow: '4px 0 12px rgba(24, 99, 220, 0.15)'
        },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <img 
          src="/nkp-logo.svg"
          alt="NKP Medical"
          style={{
            width: '100%',
            maxWidth: '200px',
            height: 'auto',
            marginBottom: '8px',
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontWeight: 400,
            mb: 0.5,
            letterSpacing: '0.05em',
            color: 'white'
          }}
        >
          Nerv
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />

      {/* Navigation Menu */}
      <List sx={{ px: 2, py: 2 }}>
        {/* Dashboard */}
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            onClick={() => navigate('/dashboard')}
            sx={{
              borderRadius: 2,
              mb: 1,
              bgcolor: isActive('/dashboard') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'secondary.main', minWidth: 40 }}>
              <Dashboard />
            </ListItemIcon>
            <ListItemText 
              primary="Dashboard" 
              primaryTypographyProps={{
                fontWeight: isActive('/dashboard') ? 600 : 400
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Accounts Section */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setSitesOpen(!sitesOpen)}
            sx={{
              borderRadius: 2,
              mb: 1,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <Language />
            </ListItemIcon>
            <ListItemText
              primary="Accounts"
              primaryTypographyProps={{ fontWeight: 500 }}
            />
            {sitesOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Collapse in={sitesOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              onClick={() => navigate('/sites')}
              sx={{
                pl: 6,
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isActive('/sites') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', minWidth: 36 }}>
                <ListAlt />
              </ListItemIcon>
              <ListItemText
                primary="All Accounts"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isActive('/sites') ? 600 : 400
                }}
              />
            </ListItemButton>

            <ListItemButton
              onClick={() => navigate('/add-site')}
              sx={{
                pl: 6,
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isActive('/add-site') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'secondary.main', minWidth: 36 }}>
                <Add />
              </ListItemIcon>
              <ListItemText
                primary="Add New Account"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isActive('/add-site') ? 600 : 400
                }}
              />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Domains */}
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            onClick={() => navigate('/domains')}
            sx={{
              borderRadius: 2,
              mb: 1,
              bgcolor: isActive('/domains') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'secondary.main', minWidth: 40 }}>
              <Public />
            </ListItemIcon>
            <ListItemText
              primary="Domains"
              primaryTypographyProps={{
                fontWeight: isActive('/domains') ? 600 : 400
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* WP Engine */}
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            onClick={() => navigate('/wpengine')}
            sx={{
              borderRadius: 2,
              mb: 1,
              bgcolor: isActive('/wpengine') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'secondary.main', minWidth: 40 }}>
              <CloudQueue />
            </ListItemIcon>
            <ListItemText
              primary="WP Engine"
              primaryTypographyProps={{
                fontWeight: isActive('/wpengine') ? 600 : 400
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Contacts Section */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setContactsOpen(!contactsOpen)}
            sx={{
              borderRadius: 2,
              mb: 1,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <ContactMail />
            </ListItemIcon>
            <ListItemText
              primary="Contacts"
              primaryTypographyProps={{ fontWeight: 500 }}
            />
            {contactsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Collapse in={contactsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              onClick={() => navigate('/contacts')}
              sx={{
                pl: 6,
                borderRadius: 2,
                mb: 0.5,
                bgcolor: (location.pathname === '/contacts') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', minWidth: 36 }}>
                <ListAlt />
              </ListItemIcon>
              <ListItemText
                primary="All Contacts"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: (location.pathname === '/contacts') ? 600 : 400
                }}
              />
            </ListItemButton>

            <ListItemButton
              onClick={() => navigate('/contacts/link-review')}
              sx={{
                pl: 6,
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isActive('/contacts/link-review') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'secondary.main', minWidth: 36 }}>
                <LinkIcon />
              </ListItemIcon>
              <ListItemText
                primary="Link Review"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isActive('/contacts/link-review') ? 600 : 400
                }}
              />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Subscriptions */}
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            onClick={() => navigate('/subscriptions')}
            sx={{
              borderRadius: 2,
              mb: 1,
              bgcolor: isActive('/subscriptions') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'secondary.main', minWidth: 40 }}>
              <Subscriptions />
            </ListItemIcon>
            <ListItemText
              primary="Subscriptions"
              primaryTypographyProps={{
                fontWeight: isActive('/subscriptions') ? 600 : 400
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Logs */}
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            onClick={() => navigate('/logs')}
            sx={{
              borderRadius: 2,
              mb: 1,
              bgcolor: isActive('/logs') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'secondary.main', minWidth: 40 }}>
              <History />
            </ListItemIcon>
            <ListItemText
              primary="System Logs"
              primaryTypographyProps={{
                fontWeight: isActive('/logs') ? 600 : 400
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Site Forms Section */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setFormsOpen(!formsOpen)}
            sx={{
              borderRadius: 2,
              mb: 1,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <Assignment />
            </ListItemIcon>
            <ListItemText 
              primary="Site Forms" 
              primaryTypographyProps={{ fontWeight: 500 }}
            />
            {formsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Collapse in={formsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              onClick={() => navigate('/forms')}
              sx={{
                pl: 6,
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isActive('/forms') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', minWidth: 36 }}>
                <Assignment />
              </ListItemIcon>
              <ListItemText 
                primary="Form Entries" 
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isActive('/forms') ? 600 : 400
                }}
              />
            </ListItemButton>

            <ListItemButton
              onClick={() => navigate('/site-forms/list')}
              sx={{
                pl: 6,
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isActive('/site-forms/list') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', minWidth: 36 }}>
                <ListAlt />
              </ListItemIcon>
              <ListItemText 
                primary="List Site Forms" 
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isActive('/site-forms/list') ? 600 : 400
                }}
              />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Admin Panel Section - Only for admin users */}
        {isAdmin && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setAdminOpen(!adminOpen)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  <AdminPanelSettings />
                </ListItemIcon>
                <ListItemText 
                  primary="Admin Panel" 
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
                {adminOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>

            <Collapse in={adminOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton
                  onClick={() => navigate('/admin/database')}
                  sx={{
                    pl: 6,
                    borderRadius: 2,
                    mb: 0.5,
                    bgcolor: isActive('/admin/database') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'secondary.main', minWidth: 36 }}>
                    <Storage />
                  </ListItemIcon>
                  <ListItemText
                    primary="Database Config"
                    secondary="Manage database connections"
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isActive('/admin/database') ? 600 : 400
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.6)'
                    }}
                  />
                </ListItemButton>

                <ListItemButton
                  onClick={() => navigate('/admin/disallowed-list')}
                  sx={{
                    pl: 6,
                    borderRadius: 2,
                    mb: 0.5,
                    bgcolor: isActive('/admin/disallowed-list') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'error.main', minWidth: 36 }}>
                    <Block />
                  </ListItemIcon>
                  <ListItemText
                    primary="Disallowed List"
                    secondary="Block email addresses"
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isActive('/admin/disallowed-list') ? 600 : 400
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.6)'
                    }}
                  />
                </ListItemButton>

                <ListItemButton
                  onClick={() => navigate('/admin/site-health')}
                  sx={{
                    pl: 6,
                    borderRadius: 2,
                    mb: 0.5,
                    bgcolor: isActive('/admin/site-health') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'success.main', minWidth: 36 }}>
                    <HealthAndSafety />
                  </ListItemIcon>
                  <ListItemText
                    primary="Site Health"
                    secondary="Monitor plugin connections"
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isActive('/admin/site-health') ? 600 : 400
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.6)'
                    }}
                  />
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}
      </List>

      {/* Bottom Section */}
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />

        {/* Dark Mode Toggle */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isDarkMode}
                onChange={toggleDarkMode}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#82c0c7',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#82c0c7',
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isDarkMode ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  {isDarkMode ? 'Dark Mode' : 'Light Mode'}
                </Typography>
              </Box>
            }
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem'
              }
            }}
          />
        </Box>

        {/* User Profile */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            mb: 2
          }}
        >
          <Avatar 
            sx={{ 
              width: 36, 
              height: 36, 
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              fontSize: '0.9rem',
              fontWeight: 600,
              mr: 2
            }}
          >
            {user?.display_name?.charAt(0) || 'U'}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {user?.display_name || 'User'}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {user?.email || 'user@nkpmedical.com'}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setLogoutConfirmOpen(true)}
            size="small"
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <ExitToApp fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Drawer>

    <ConfirmDialog
      open={logoutConfirmOpen}
      title="Sign Out"
      message="Are you sure you want to sign out?"
      onConfirm={() => { setLogoutConfirmOpen(false); handleLogout(); }}
      onCancel={() => setLogoutConfirmOpen(false)}
      confirmLabel="Yes, Sign Out"
      cancelLabel="Cancel"
    />
    </>
  );
};

export { DRAWER_WIDTH };
export default Sidebar;